import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { InputMask } from 'primereact/inputmask';
import { Accordion, AccordionTab } from 'primereact/accordion';
import * as Grouped from '../Atoms/Grouped';
import _ from 'lodash';
import dayjs from 'dayjs';
import { YN, VEHICLE_TYPE_LEVEL1 } from '../../constants/Constants';
import { PC_BASE_BLUEPRINT } from '../../constants/PerformanceCheckConstants';

const baseTitles = _.mapValues(PC_BASE_BLUEPRINT, 'title');
const baseValueItems = _.mapValues(PC_BASE_BLUEPRINT, 'valueItems');

const VehicleInformationDialog = ({ visible, onHide, vehicleData, workDetails, vehicleMaintenance }) => {
  if (!vehicleData) return null;

  // 날짜 포맷팅 (YYYYMMDD -> YYYY-MM-DD)
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return '';
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  };

  // Y/N 값을 한글로 변환
  const getYesNoLabel = (value) => {
    if (value === YN.YES || value === 'Y' || value === '1') return '있음';
    if (value === YN.NO || value === 'N' || value === '0') return '없음';
    return '해당없음';
  };

  // 차종구분 라벨 찾기
  const getMadeAtLabel = (value) => {
    const item = VEHICLE_TYPE_LEVEL1.find((el) => el.value === value);
    return item?.label || value || '-';
  };

  // 코드를 라벨로 변환
  const getCodeLabel = (code, codeType) => {
    const items = baseValueItems[codeType];
    if (!items) return code || '-';
    const item = items.find((el) => el.value === code);
    return item?.label || code || '-';
  };

  return (
    <Dialog
      header="차량정보"
      visible={visible && !!vehicleData}
      style={{ width: '90vw', maxWidth: '1400px' }}
      breakpoints={{ '960px': '95vw' }}
      onHide={onHide}
      modal
      footer={() => (
        <div className="flex flex-auto align-items-start justify-content-start pt-4">
          <Button
            type="button"
            label="닫기"
            className="p-button-outlined"
            onClick={onHide}
          />
        </div>
      )}
    >
      {vehicleData ? (
        <>
          <Panel header="자동차의 기본 상태" className="shadow-2">
            <div className="grid">
              {/* 차대번호 */}
              <div className="col-12 sm:col-12 md:col-6 lg:col-3 mb-2">
                <Grouped.TitledInputText
                  boldStyle={true}
                  id="VIN"
                  title={baseTitles['VIN']}
                  value={vehicleData.VIN || vehicleData.VHRNO || '-'}
                  readOnly
                  className="text-lg md:text-base"
                />
              </div>

              {/* 연식 */}
              <div className="col-12 sm:col-12 md:col-6 lg:col-3 mb-2">
                <Grouped.TitledInputText
                  id="PRYE"
                  title={baseTitles['PRYE']}
                  value={vehicleData.PRYE || '-'}
                  readOnly
                  className="text-lg md:text-base"
                />
              </div>

              {/* 차명 */}
              <div className="col-12 sm:col-12 md:col-6 lg:col-3 mb-2">
                <Grouped.TitledInputText
                  boldStyle={true}
                  id="CNM"
                  title={baseTitles['CNM']}
                  value={vehicleData.CNM || '-'}
                  readOnly
                  className="text-lg md:text-base"
                />
              </div>

              {/* 최초등록일 */}
              <div className="col-12 sm:col-12 md:col-6 lg:col-3 mb-2">
                <div className="flex">
                  <span
                    className="h-full p-inputgroup-addon w-full font-medium text-center min-w-3 w-4 max-w-5 text-lg md:text-base"
                    style={{ display: 'table' }}
                  >
                    <span
                      style={{
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        wordBreak: 'keep-all',
                      }}
                    >
                      최초등록일
                    </span>
                  </span>
                  <div className="p-inputgroup">
                    <InputMask
                      value={formatDate(vehicleData.FRST_REGIST_DE)}
                      className="bg-gray-100 text-lg md:text-base"
                      mask="9999-99-99"
                      slotChar="yyyy-mm-dd"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* 원동기형식 */}
              <div className="col-12 sm:col-12 md:col-6 lg:col-3 mb-2">
                <Grouped.TitledInputText
                  id="MTRS_FOM"
                  title={baseTitles['MTRS_FOM']}
                  value={vehicleData.MTRS_FOM_NM || vehicleData.MTRS_FOM || '-'}
                  readOnly
                  className="text-lg md:text-base"
                />
              </div>

              {/* 검사유효기간 */}
              <div className="col-12 sm:col-12 md:col-12 lg:col-6 mb-2">
                <div className="flex">
                  <span
                    className="h-full p-inputgroup-addon w-full font-medium text-center min-w-3 w-4 max-w-5 text-lg md:text-base"
                    style={{ display: 'table' }}
                  >
                    <span
                      style={{
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        wordBreak: 'keep-all',
                      }}
                    >
                      검사유효기간
                    </span>
                  </span>
                  <div className="p-inputgroup">
                    <InputMask
                      value={formatDate(vehicleData.INSPT_VALID_PD_BGNDE)}
                      mask="9999-99-99"
                      slotChar="yyyy-mm-dd"
                      readOnly
                      className="bg-gray-100 text-lg md:text-base"
                    />
                    <span className="p-inputgroup-addon w-min"> ~ </span>
                    <InputMask
                      value={formatDate(vehicleData.INSPT_VALID_PD_ENDDE)}
                      mask="9999-99-99"
                      slotChar="yyyy-mm-dd"
                      readOnly
                      className="bg-gray-100 text-lg md:text-base"
                    />
                  </div>
                </div>
              </div>

              {/* 최근 주행 거리 */}
              <div className="col-12 sm:col-6 lg:col-3 mb-2">
                <div className="relative h-full">
                  <p
                    className="absolute"
                    style={{
                      right: '0',
                      marginRight: '5px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      userSelect: 'none',
                    }}
                  >
                    Km
                  </p>
                  <Grouped.TitledInputNumber
                    id="LATEST_TRVL_DSTNC"
                    className="bg-gray-100 pr-5 text-lg md:text-base"
                    title={baseTitles['LATEST_TRVL_DSTNC']}
                    value={vehicleData.TRVL_DSTNC ? Number(vehicleData.TRVL_DSTNC) : 0}
                    readOnly
                  />
                </div>
              </div>

              {/* 차종구분 (국산/수입) */}
              <div className="col-12 sm:col-6 lg:col-3 mb-2">
                <Grouped.TitledInputText
                  id="madeAt"
                  title="차종구분"
                  value={getMadeAtLabel(vehicleData.madeAt || 'D')}
                  readOnly
                  className="text-lg md:text-base"
                />
              </div>

              {/* 차종코드 (승용, 승합 등) - 값이 있을 때만 표시 */}
              {(vehicleData.VHCTY_ASORT_CODE || vehicleData.carAsort) && (
                <div className="col-12 sm:col-6 lg:col-3 mb-2">
                  <Grouped.TitledInputText
                    id="VHCTY_ASORT_CODE"
                    title={baseTitles['VHCTY_ASORT_CODE']}
                    value={getCodeLabel(vehicleData.VHCTY_ASORT_CODE || vehicleData.carAsort, 'VHCTY_ASORT_CODE')}
                    readOnly
                    className="text-lg md:text-base"
                  />
                </div>
              )}

              {/* 연료구분 */}
              <div className="flex flex-column gap-1 col-12 sm:col-12 lg:col-4 mb-2">
                <Grouped.TitledInputText
                  id="USE_FUEL_CODE"
                  title="연료구분"
                  value={getCodeLabel(vehicleData.USE_FUEL_CODE, 'USE_FUEL_CODE')}
                  readOnly
                  className="text-lg md:text-base"
                />
                <Grouped.TitledInputText
                  boldStyle={true}
                  id="USE_FUEL_NM"
                  title="연료상세"
                  value={vehicleData.USE_FUEL_NM || '-'}
                  readOnly
                  className="text-lg md:text-base"
                />
              </div>

              {/* 배기량 */}
              <div className="col-12 sm:col-6 lg:col-4 mb-2">
                <Grouped.TitledInputNumber
                  id="displacement"
                  title="배기량"
                  suffix="cc"
                  value={vehicleData.displacement ? Number(vehicleData.displacement) : 0}
                  readOnly
                  classNameProps={true}
                  className="border-primary text-lg font-bold"
                  min={0}
                  max={200000}
                />
              </div>

              {/* 승차 인원 */}
              <div className="col-12 sm:col-6 lg:col-4 mb-2">
                <Grouped.TitledInputNumber
                  id="maxPerson"
                  title="승차 인원"
                  suffix="명 이하"
                  value={vehicleData.maxPerson ? Number(vehicleData.maxPerson) : 0}
                  readOnly
                  classNameProps={true}
                  className="border-primary text-lg font-bold"
                  min={0}
                  max={99}
                />
              </div>

              {/* 변속기종류 */}
              <div className="col-12">
                <Grouped.TitledRadioButton
                  title={baseTitles['GRBX_KND_CODE']}
                  id="GRBX_KND_CODE"
                  value={vehicleData.GRBX_KND_CODE || ''}
                  items={baseValueItems['GRBX_KND_CODE']}
                  disabled
                />
              </div>

              {/* 리콜 여부 */}
              <div className="col-12 sm:col-12 lg:col-4 mb-2">
                <Grouped.TitledRadioButton
                  title={baseTitles['RECALL_TRGET_AT']}
                  id="RECALL_TRGET_AT"
                  value={vehicleData.RECALL_TRGET_AT || '0'}
                  items={baseValueItems['RECALL_TRGET_AT']}
                  disabled
                />
              </div>

              {/* 리콜이행 여부 - 리콜대상 여부가 있을 때만 표시 */}
              {(vehicleData.RECALL_TRGET_AT === '1' || vehicleData.RECALL_TRGET_AT === YN.YES) && (
                <div className="col-12 sm:col-12 lg:col-4 mb-2">
                  <Grouped.TitledRadioButton
                    title={baseTitles['RECALL_FLFL_AT'] || '리콜이행 여부'}
                    id="RECALL_FLFL_AT"
                    value={vehicleData.RECALL_FLFL_AT || '0'}
                    items={baseValueItems['RECALL_FLFL_AT'] || [
                      { label: '없음', value: '0' },
                      { label: '있음', value: '1' },
                    ]}
                    disabled
                  />
                </div>
              )}

              {/* 튜닝 여부 */}
              <div className="col-12 sm:col-12 lg:col-4 mb-2">
                <Grouped.TitledRadioButton
                  title={baseTitles['STMD_AT']}
                  id="STMD_AT"
                  value={vehicleData.STMD_AT || 'N'}
                  items={baseValueItems['STMD_AT']}
                  disabled
                />
              </div>

              {/* 튜닝 적법여부 - 튜닝여부가 있을 때만 표시 */}
              {(vehicleData.STMD_AT === 'Y' || vehicleData.STMD_AT === YN.YES) && (
                <div className="col-12 sm:col-12 lg:col-4 mb-2">
                  <Grouped.TitledRadioButton
                    title={baseTitles['UNLAW_STMD_AT'] || '튜닝 적법 여부'}
                    id="UNLAW_STMD_AT"
                    value={vehicleData.UNLAW_STMD_AT || '0'}
                    items={baseValueItems['UNLAW_STMD_AT'] || [
                      { label: '적법', value: '1' },
                      { label: '불법', value: '2' },
                    ]}
                    disabled
                  />
                </div>
              )}

              {/* 튜닝 항목 구분 - 튜닝여부가 있을 때만 표시 */}
              {(vehicleData.STMD_AT === 'Y' || vehicleData.STMD_AT === YN.YES) && (
                <div className="col-12 sm:col-12 lg:col-4 mb-2">
                  <Grouped.TitledRadioButton
                    title={baseTitles['UNLAW_STMD_IEM_SE_CODE'] || '튜닝 항목 구분'}
                    id="UNLAW_STMD_IEM_SE_CODE"
                    value={vehicleData.UNLAW_STMD_IEM_SE_CODE || '0'}
                    items={baseValueItems['UNLAW_STMD_IEM_SE_CODE'] || [
                      { label: '구조', value: '1' },
                      { label: '장치', value: '2' },
                    ]}
                    disabled
                  />
                </div>
              )}

              {/* 용도 변경 여부 */}
              <div className="col-12 sm:col-12 lg:col-4 mb-2">
                <Grouped.TitledRadioButton
                  title={baseTitles['PRPOS_CHANGE_SE_AT']}
                  id="PRPOS_CHANGE_SE_AT"
                  value={vehicleData.PRPOS_CHANGE_SE_AT || 'N'}
                  items={baseValueItems['PRPOS_CHANGE_SE_AT']}
                  disabled
                />
              </div>

              {/* 용도 변경 구분코드 - 용도변경 여부가 있을 때만 표시 */}
              {(vehicleData.PRPOS_CHANGE_SE_AT === 'Y' || vehicleData.PRPOS_CHANGE_SE_AT === YN.YES) && (
                <div className="col-12 sm:col-12 lg:col-4 mb-2">
                  <Grouped.TitledRadioButton
                    title={baseTitles['PRPOS_CHANGE_SE_CODE'] || '용도 변경 구분 코드'}
                    id="PRPOS_CHANGE_SE_CODE"
                    value={vehicleData.PRPOS_CHANGE_SE_CODE || '0'}
                    items={baseValueItems['PRPOS_CHANGE_SE_CODE'] || [
                      { label: '렌트', value: '1' },
                      { label: '영업용', value: '3' },
                    ]}
                    disabled
                  />
                </div>
              )}

              {/* 특별이력 (침수/화재) */}
              <div className="col-12 sm:col-12 lg:col-4 mb-2">
                <Grouped.TitledRadioButton
                  title={baseTitles['FLUD_AT'] || '특별 이력'}
                  id="FLUD_AT"
                  value={vehicleData.FLUD_AT || '0'}
                  items={baseValueItems['FLUD_AT'] || [
                    { label: '없음', value: '0' },
                    { label: '침수', value: '1' },
                    { label: '화재', value: '2' },
                  ]}
                  disabled
                />
              </div>
            </div>
          </Panel>

        {/* 차량정비이력 */}
        <Panel header="차량정비이력" className="shadow-2 mt-3">
          {workDetails && workDetails.length > 0 ? (
            <Accordion multiple>
              {_.map(
                _.sortBy(workDetails, 'workRequestDate'),
                (workDetail, idx) => {
                  const rawRequestDate = _.get(workDetail, 'workRequestDate');
                  const rawCompleteDate = _.get(workDetail, 'workCompleteDate');
                  const requestDate = rawRequestDate && dayjs(rawRequestDate).isValid()
                    ? dayjs(rawRequestDate).format('YYYY-MM-DD')
                    : '정보 없음';
                  const completeDate = rawCompleteDate && dayjs(rawCompleteDate).isValid()
                    ? dayjs(rawCompleteDate).format('YYYY-MM-DD')
                    : '정보 없음';
                  return (
                    <AccordionTab
                      header={<p>{requestDate}</p>}
                      key={`repair_${idx}`}
                      className="text-lg md:text-base"
                    >
                      <p>
                        <strong>수리 요청일 :</strong> {requestDate}
                      </p>
                      <p>
                        <strong>수리 완료일 :</strong> {completeDate}
                      </p>
                      {_.map(
                        _.get(workDetail, 'workItems'),
                        (workItem, itemIdx) => {
                          return (
                            <p key={`repair_text_${idx}_${itemIdx}`}>
                              {workItem?.fullText}
                            </p>
                          );
                        }
                      )}
                    </AccordionTab>
                  );
                }
              )}
            </Accordion>
          ) : (
            <div className="py-4 text-center text-gray-500">정비 이력이 없습니다.</div>
          )}
        </Panel>

        </>
      ) : (
        <div className="p-3 text-center">차량정보가 없습니다.</div>
      )}
    </Dialog>
  );
};

export default VehicleInformationDialog;
